/**
 * Environment Variable Schema
 *
 * Manual validation (no Zod) so that we don't ship the validator library
 * (~131KB raw / ~50KB minified) to every visitor just to check 8 fields
 * at startup. Server-side code in functions/api/ may still use Zod
 * directly without affecting the frontend bundle.
 */

export type SentryEnvironment = 'development' | 'staging' | 'production';

export interface Env {
  // Supabase (Required)
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;

  // Sentry (Optional)
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT: SentryEnvironment;

  // Analytics (Optional)
  VITE_PLAUSIBLE_DOMAIN?: string;

  // Feature Flags (Optional) — kept as raw string at runtime;
  // call sites coerce to boolean as needed.
  VITE_SHOP_ENABLED?: string;

  // Payment Gateways (Optional - publishable client-side keys)
  VITE_STRIPE_PUBLISHABLE_KEY?: string;
  VITE_PAYPAL_CLIENT_ID?: string;
}

const SENTRY_ENVIRONMENTS: ReadonlySet<SentryEnvironment> = new Set([
  'development',
  'staging',
  'production',
]);

const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validates environment variables.
 * Throws an Error listing all issues if validation fails.
 */
export function validateEnv(): Env {
  const raw = import.meta.env as Record<string, string | undefined>;
  const issues: string[] = [];

  const supabaseUrl = raw.VITE_SUPABASE_URL;
  if (!supabaseUrl || !isValidUrl(supabaseUrl)) {
    issues.push('  - VITE_SUPABASE_URL: must be a valid URL');
  }

  const supabaseKey = raw.VITE_SUPABASE_ANON_KEY;
  if (!supabaseKey) {
    issues.push('  - VITE_SUPABASE_ANON_KEY: is required');
  }

  if (raw.VITE_SENTRY_DSN && !isValidUrl(raw.VITE_SENTRY_DSN)) {
    issues.push('  - VITE_SENTRY_DSN: must be a valid URL when set');
  }

  const env = raw.VITE_SENTRY_ENVIRONMENT;
  if (env && !SENTRY_ENVIRONMENTS.has(env as SentryEnvironment)) {
    issues.push(
      "  - VITE_SENTRY_ENVIRONMENT: must be one of 'development', 'staging', 'production'"
    );
  }

  if (raw.VITE_STRIPE_PUBLISHABLE_KEY && !raw.VITE_STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
    issues.push('  - VITE_STRIPE_PUBLISHABLE_KEY: must start with "pk_"');
  }

  if (issues.length > 0) {
    console.error('❌ Environment validation failed:\n' + issues.join('\n'));
    throw new Error('Invalid environment configuration. Check console for details.');
  }

  return {
    VITE_SUPABASE_URL: supabaseUrl as string,
    VITE_SUPABASE_ANON_KEY: supabaseKey as string,
    VITE_SENTRY_DSN: raw.VITE_SENTRY_DSN || undefined,
    VITE_SENTRY_ENVIRONMENT: (env as SentryEnvironment) || 'development',
    VITE_PLAUSIBLE_DOMAIN: raw.VITE_PLAUSIBLE_DOMAIN || undefined,
    VITE_SHOP_ENABLED: raw.VITE_SHOP_ENABLED || undefined,
    VITE_STRIPE_PUBLISHABLE_KEY: raw.VITE_STRIPE_PUBLISHABLE_KEY || undefined,
    VITE_PAYPAL_CLIENT_ID: raw.VITE_PAYPAL_CLIENT_ID || undefined,
  };
}

/**
 * Get environment variables with type safety.
 * Only call this after validateEnv() has been called at app startup.
 */
export function getEnv(): Env {
  return import.meta.env as unknown as Env;
}
